import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Controller, { inject as controller } from '@ember/controller';
import { task } from 'ember-concurrency';
import FormValidations from 'cio/utils/form-validations';
import RecaptchaService from 'cio/services/recaptcha';
import AnalyticsService from 'cio/services/analytics';
import RouterService from '@ember/routing/router-service';
import SignupController from 'cio/pods/signup/controller';
import { ProductApp } from 'cio/utils/product-app-info';
import PostRegistrationController from '../controller';
import SignupService from 'cio/services/signup';
import ApiErrorsService from 'cio/services/api-errors';
import moment from 'moment-timezone';
import ExperimentsService from 'cio/services/experiments';

const Validations = {
  integrationExperience(value: string) {
    if (!value?.trim()) {
      return "Can't be blank";
    }

    return true;
  },
};

export default class PostRegistrationConnect extends Controller {
  validations!: FormValidations;

  @service recaptcha!: RecaptchaService;
  @service analytics!: AnalyticsService;
  @service router!: RouterService;
  @service signup!: SignupService;
  @service apiErrors!: ApiErrorsService;
  @service experiments!: ExperimentsService;

  declare model: {
    signupType: {
      name: string;
      isJourneys: boolean;
      isDataPipelines: boolean;
    };
  };

  @controller('signup') signupController!: SignupController;
  @controller('authenticated/post-registration') postRegistrationController!: PostRegistrationController;

  queryParams = ['signupType'];

  get product() {
    return this.signupController.product;
  }

  get isSubmitDisabled() {
    return this.submitTask.isRunning || this.validations.hasErrors;
  }

  constructor() {
    super(...arguments);
    this.set(
      'validations',
      FormValidations.create({ validations: Validations, context: this, modelKey: 'signupController.formValues' }),
    );
  }

  submitTask = task(async (ev: MouseEvent) => {
    ev.preventDefault();
    let start = window.performance.now();
    try {
      await this.validations.validateModel();
      let { formValues } = this.signupController;

      await this.postRegistrationController.updateSetupGuideTask.perform('startedAt', moment().unix());

      // set useCase for the setup guide
      await this.postRegistrationController.updateSetupGuideTask.perform('useCase', formValues.useCase);

      // set personalization change steps order for journeys setup guide
      if (this.product.name === ProductApp.JOURNEYS && formValues.integrationExperience === 'experienced_implementer') {
        await this.postRegistrationController.updateSetupGuideTask.perform('personalizationChangeStepsOrder', true);
      }

      // pre-select channels in the setup guide
      let allSelectedChannels = formValues.channels;
      if (allSelectedChannels.includes('other')) {
        allSelectedChannels = allSelectedChannels.filter((c) => c !== 'other');
      }
      await this.postRegistrationController.updateSetupGuideMessagingChannelsTask.perform(allSelectedChannels);

      await this.signup.postRegistration({
        integrationExperience: formValues.integrationExperience,
        automationExperience: formValues.automationExperience,
        useCase: formValues.useCase,
        useCaseDetail: formValues.useCaseDetail,
        role: formValues.role,
        channels: formValues.channels,
      });
      await this.trackIntegrationExperienceSubmitted();
    } catch (err) {
      this.apiErrors.report(err);
      throw err;
    }

    await this.analytics.track('signup', 'signup_completed', {
      signupType: 'journeys',
      fakeDoorExperiment: true,
      campaignTemplateGalleryExperiment: this.experiments.getExperimentVariation('campaignTemplateGalleryExperiment'),
      testDeliveryMode: this.signupController?.formValues?.testDeliveryMode,
    });

    let goToAccountDelay = window.performance.now() - start;

    let lastAccountReadyTask = await this.postRegistrationController.waitForAccountToBeReadyTask.lastSuccessful;
    let accountReadyDuration = lastAccountReadyTask?.elapsedTime ?? 0;

    this.analytics.track('performance', 'signup', {
      goToAccountDelay,
      accountReadyDuration,
      signupType: this.signupController.model?.signupType?.name,
    });

    this.router.transitionTo('app');
  });

  trackIntegrationExperienceSubmitted() {
    return this.analytics.track('signup', 'connecting_data_submitted', {
      integrationExperience: this.signupController.formValues.integrationExperience,
      signupType: this.signupController.model?.signupType?.name,
    });
  }

  @action
  onValueChange(field: keyof SignupController['formValues'], value: string) {
    this.signupController.setFormValue(field, value);
    this.validations.validateAttribute(field);
  }
}
